import { Message, Conversation } from '../types/MessageTypes';
import api from './api.interceptor';
import { API_BASE_URL } from '../config/api.config';

// Chat backend not implemented yet — always use localStorage mock
const USE_MOCK = true;

class ChatService {
  private baseUrl = `${API_BASE_URL}/chat`;

  async getConversations(): Promise<Conversation[]> {
    if (USE_MOCK) {
      return JSON.parse(localStorage.getItem('chat_conversations') || '[]');
    }

    try {
      const response = await api.get(`${this.baseUrl}/conversations`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  }

  async getMessages(referralId: string): Promise<Message[]> {
    if (USE_MOCK) {
      const all: Message[] = JSON.parse(localStorage.getItem('chat_messages') || '[]');
      return all.filter(m => m.referral_id === referralId);
    }

    try {
      const response = await api.get(`${this.baseUrl}/messages/${referralId}`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  }

  async sendMessage(referralId: string, content: string): Promise<Message> {
    const msg: Message = {
      id: `msg_${Date.now()}`,
      referral_id: referralId,
      sender_id: 'mock-user',
      sender_name: 'Dev User',
      content,
      created_at: new Date().toISOString(),
      read: false,
    };

    if (USE_MOCK) {
      const messages: Message[] = JSON.parse(localStorage.getItem('chat_messages') || '[]');
      messages.push(msg);
      localStorage.setItem('chat_messages', JSON.stringify(messages));

      // Update or create conversation
      const convos: Conversation[] = JSON.parse(localStorage.getItem('chat_conversations') || '[]');
      const existing = convos.find(c => c.referral_id === referralId);
      if (existing) {
        existing.last_message = content;
        existing.last_message_at = msg.created_at;
      } else {
        convos.unshift({
          referral_id: referralId,
          vacancy_title: 'Vacancy',
          candidate_name: 'Candidate',
          participants: [{ id: 'mock-user', name: 'Dev User' }],
          last_message: content,
          last_message_at: msg.created_at,
          unread_count: 0,
        });
      }
      localStorage.setItem('chat_conversations', JSON.stringify(convos));
      return msg;
    }

    const response = await api.post(`${this.baseUrl}/messages/${referralId}`, { content });
    if (!response.ok) throw new Error('Failed to send message');
    return await response.json();
  }

  async markAsRead(referralId: string): Promise<void> {
    if (USE_MOCK) {
      const messages: Message[] = JSON.parse(localStorage.getItem('chat_messages') || '[]');
      messages.forEach(m => {
        if (m.referral_id === referralId) m.read = true;
      });
      localStorage.setItem('chat_messages', JSON.stringify(messages));

      const convos: Conversation[] = JSON.parse(localStorage.getItem('chat_conversations') || '[]');
      const convo = convos.find(c => c.referral_id === referralId);
      if (convo) convo.unread_count = 0;
      localStorage.setItem('chat_conversations', JSON.stringify(convos));
      return;
    }

    await api.post(`${this.baseUrl}/read/${referralId}`, {});
  }

  async getUnreadCount(): Promise<number> {
    if (USE_MOCK) {
      const convos: Conversation[] = JSON.parse(localStorage.getItem('chat_conversations') || '[]');
      return convos.reduce((sum, c) => sum + c.unread_count, 0);
    }

    try {
      const response = await api.get(`${this.baseUrl}/unread-count`);
      if (!response.ok) return 0;
      const data = await response.json();
      return data.count || 0;
    } catch {
      return 0;
    }
  }
}

export const chatService = new ChatService();
export default chatService;
